import { useNavigate } from "@tanstack/react-router";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { PageHeader, ReadOnlyBanner, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/datos-generales";
import { DatosGeneralesForm } from "./DatosGeneralesForm";

/**
 * Container de la pantalla de datos generales · sin lógica de negocio.
 * Compone PageHeader, stepper y form. La mutación / navegación las
 * inyecta al form vía props.
 */
export function DatosGeneralesPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (P29) · sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;

  // URL del step anterior · depende del subtipo (decisión 51):
  //   - Múltiples CFDI → /cargar-zip (carga ZIP de CFDIs)
  //   - Contrapago (P44) → /organizacion-manual (no hay CFDI que cargar)
  //   - CFDI único (default) → /cargar-cfdi
  const urlCargaCFDI =
    sol.subtipo === "MULTIPLES_CFDI"
      ? `/solicitudes/${id}/cargar-zip`
      : sol.subtipo === "CONTRAPAGO"
        ? `/solicitudes/${id}/organizacion-manual`
        : sol.subtipo === "INVOICE"
          ? `/solicitudes/${id}/carga-invoice`
          : `/solicitudes/${id}/cargar-cfdi`;

  // CA/OC Invoice: el proveedor sale del contrato/orden · no hay paso de proveedor,
  // se salta directo al prorrateo (contra el total extraído del PDF).
  const esInvoiceConDocumento =
    sol.subtipo === "INVOICE" && (sol.tipo === "ContratoAnualizado" || sol.tipo === "OrdenDeCompra");

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Datos generales"
        description="FB60 · captura motivo de pago, periodo de servicio y área (decisión 42)."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: id.slice(0, 8), href: `/solicitudes/${id}` },
          { label: "Datos generales" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="datos" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="Los datos generales no pueden modificarse." />}
          <DatosGeneralesForm
            solicitudId={id}
            iniciales={sol.datosGenerales}
            soloLectura={soloLectura}
            labelVolver={
              sol.subtipo === "CONTRAPAGO"
                ? "Regresar a Organización"
                : sol.subtipo === "INVOICE"
                  ? "Regresar a carga de PDF Invoice"
                  : undefined
            }
            onVolver={() => navigate({ to: urlCargaCFDI as never })}
            onSuccess={(actualizada) =>
              navigate({
                // Invoice CA/OC con prorrateo automático: ya confirmado · sigue Documentos.
                to: esInvoiceConDocumento && actualizada.prorrateoAutomatico
                  ? "/solicitudes/$id/documentos"
                  : esInvoiceConDocumento
                  ? "/solicitudes/$id/prorrateo-sin-cfdi"
                  : sol.subtipo === "CONTRAPAGO" || sol.subtipo === "INVOICE"
                    ? "/solicitudes/$id/proveedor-manual"
                    : "/solicitudes/$id/proveedor",
                params: { id },
              })
            }
          />
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
